import { clsx, type ClassValue } from 'clsx';
import { ChevronDown } from 'lucide-react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { AnimatedHeight } from './AnimatedHeight';
import type { ExtrasProduct } from './types';

/** Same helper both apps have in `lib/utils`, duplicated rather than imported —
 * this package can't reach into an app, and it's two lines. */
export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/**
 * Which page the section is sitting on.
 *
 * `sunken` is the original: a lifted panel inside the v1 offer card, carrying
 * its own top margin because it was mounted between that card's rows.
 *
 * `card` is the frame the v2 pages are built from — `Card` + `SubHead` in
 * `@codezo/quote-detail`, matched here down to the padding, the type scale and
 * the radius token, so a section standing among v2 cards is one of them rather
 * than a panel someone forgot to convert. No top margin: on those pages it is a
 * sibling in a stack that owns its own gap.
 *
 * Two frames rather than one because v1 is still what `/quotes/:id` serves, and
 * a single restyle would leave that card wearing one v2 section among its own.
 * When v1 goes, so does this: keep `card`, delete the map.
 */
export type SectionFrame = 'sunken' | 'card';

const FRAME: Record<
	SectionFrame,
	{ shell: string; head: string; icon: string; title: string; meta: string; body: string }
> = {
	sunken: {
		shell: 'mt-4 rounded-lg bg-surface-sunken',
		head: 'gap-3 px-3 py-2.5 hover:bg-surface',
		icon: 'text-text-3',
		title: 'text-xs tracking-[0.14em] text-text-3',
		meta: 'text-xs',
		body: 'px-3 pt-2.5 pb-3',
	},
	card: {
		shell: 'rounded-[var(--r-3)] bg-surface',
		head: 'gap-2 px-4 py-3',
		icon: 'text-text-4',
		title: 'text-2xs tracking-[0.08em] text-text-2',
		meta: 'text-sm',
		body: 'pt-3 pb-3',
	},
};

/**
 * The frame a section's own rows are being drawn in.
 *
 * Read by the leaf marks below — the add button, the row icons, the empty state
 * — which are rendered INSIDE a `Section` and so can pick up its frame without
 * every caller threading it down. A section component itself can't: it builds
 * its children above the provider, so it reads its own `frame` prop through
 * `sectionSkin` instead.
 */
const SectionFrameContext = createContext<SectionFrame>('sunken');

/**
 * The row-level measurements that go with a frame.
 *
 * `pad` is the one that matters: a v2 card puts the gutter on the ROW, so its
 * rules run the full width of the card. The sunken panel puts it on the body and
 * the rules inset — which, sat among v2 cards, is what makes a section look like
 * it was pasted in from another page.
 */
export function sectionSkin(frame: SectionFrame = 'sunken') {
	const card = frame === 'card';
	return {
		/** Gutter for every row, including the column heads and the empty state. */
		pad: card ? 'px-4' : '',
		/** Column heads — `MicroLabel` in `@codezo/quote-detail`, matched here. */
		head: card
			? 'text-2xs font-medium tracking-[0.08em] text-text-4 uppercase'
			: 'text-2xs text-text-4',
		/** Row height, matching the offer tables beside it. */
		row: card ? 'py-2.5' : 'py-2',
	};
}

/**
 * The shell every section shares with Delivery: icon, uppercase title, a
 * subtitle saying what the section is for, and a right-aligned scope hint.
 *
 * ── Collapsing is the v1 frame's, not the section's ──
 *
 * On the v1 card these are panels stacked INSIDE another card, and an offer with
 * no samples, no adjustments and no attachments spent three screenfuls saying
 * so; folding the bodies away and keeping the counting headers is what made that
 * card readable.
 *
 * On a v2 page they are cards among cards, and nothing else there collapses —
 * plans, products, delivery and samples are all open. One accordion in a stack
 * of six plain cards reads as a component that came from somewhere else, which
 * is exactly what it was. An empty section there also costs a header and a line,
 * not a screenful. So the `card` frame doesn't collapse: no chevron, no button,
 * body always out. `defaultOpen` is ignored under it.
 */
export function Section({
	icon: Icon,
	title,
	subtitle,
	hint,
	/** Open on mount. Sections pass `false` when they have nothing in them, so a
	 * batch with no extras opens as a stack of one-line headers. Ignored by the
	 * `card` frame, which doesn't fold. */
	defaultOpen = true,
	frame = 'sunken',
	action,
	children,
}: {
	icon: React.ElementType;
	title: string;
	subtitle: string;
	hint?: string;
	defaultOpen?: boolean;
	/** Which page this is on — see `SectionFrame`. */
	frame?: SectionFrame;
	/**
	 * The section's own control, at the right of the header — where the offer's
	 * other sections keep theirs.
	 *
	 * `card` only, and not by preference: the sunken header IS a button, and a
	 * control nested inside one is neither clickable nor valid. v1 doesn't ask
	 * for one, so the restriction costs nothing until it goes.
	 */
	action?: React.ReactNode;
	children: React.ReactNode;
}) {
	const skin = FRAME[frame];
	const collapsible = frame === 'sunken';
	const [open, setOpen] = useState(defaultOpen);

	// Content arriving after mount — a refetch, the first sample added to a batch
	// — opens the section, rather than landing the change behind a shut header.
	// Never closes: once someone has opened a section it stays where they put it.
	const wasOpen = useRef(defaultOpen);
	useEffect(() => {
		if (defaultOpen && !wasOpen.current) setOpen(true);
		wasOpen.current = defaultOpen;
	}, [defaultOpen]);

	const shown = collapsible ? open : true;

	const head = (
		<>
			<Icon size={14} className={cn('shrink-0', skin.icon)} />
			<span className={cn('shrink-0 font-medium uppercase', skin.title)}>{title}</span>
			<span className={cn('min-w-0 truncate text-text-4', skin.meta)}>{subtitle}</span>
			{hint && <span className={cn('ml-auto shrink-0 text-text-4', skin.meta)}>{hint}</span>}
			{!collapsible && action && (
				/* The same slot `SubHead` gives its action, down to the inherited
				   type and colour: a ghost button sets neither, so a slot that
				   forgets them renders the identical control a shade darker than
				   the one on the card above. */
				<span className={cn('shrink-0 text-text-4', skin.meta, !hint && 'ml-auto')}>
					{action}
				</span>
			)}
			{collapsible && (
				<ChevronDown
					size={15}
					className={cn(
						'shrink-0 text-text-4 transition-transform duration-200',
						!hint && 'ml-auto',
						open && 'rotate-180'
					)}
				/>
			)}
		</>
	);

	const headClass = cn(
		'flex w-full items-center text-left',
		skin.head,
		shown && 'border-b border-line-1'
	);

	return (
		<div className={cn('overflow-hidden border border-line-1', skin.shell)}>
			{collapsible ? (
				<button
					type="button"
					onClick={() => setOpen((v) => !v)}
					aria-expanded={open}
					className={cn(headClass, 'transition-colors')}
				>
					{head}
				</button>
			) : (
				/* A heading, not a control: nothing folds here, so a button would
				   promise something that never happens — which is why the card
				   frame's head carries no hover state either. */
				<div className={headClass}>{head}</div>
			)}
			{/* Same height animation the product cards use, so opening a section
			    grows the card instead of snapping it. */}
			<SectionFrameContext.Provider value={frame}>
				{collapsible ? (
					<AnimatedHeight step={open ? 'open' : 'closed'}>
						{open && <div className={skin.body}>{children}</div>}
					</AnimatedHeight>
				) : (
					<div className={skin.body}>{children}</div>
				)}
			</SectionFrameContext.Provider>
		</div>
	);
}

/** What a section says when nothing in the batch has it. Kept rather than
 * hiding the section: "no fees or discounts" is information, and a section that
 * disappears leaves the reader unsure whether it was missing or inapplicable.
 *
 * On a v2 card it is the dashed `EmptyRow` every other section there uses; in
 * the sunken panel it stays the quiet line it has always been. */
export function EmptyState({ children }: { children: React.ReactNode }) {
	const card = useContext(SectionFrameContext) === 'card';
	return (
		<div
			className={cn(
				'text-sm text-text-4',
				card
					? 'mx-4 rounded-[var(--r-2)] border border-dashed border-line-2 px-3 py-3'
					: 'py-1'
			)}
		>
			{children}
		</div>
	);
}

/**
 * "on 2 of 4 products". Says "none" rather than dropping out when the section is
 * empty: the panel this belongs to folds, so its header can be all there is, and
 * one with no hint at all reads as a section nobody has looked at.
 *
 * The v2 card frame doesn't call this — it never folds, and the body below it
 * already says in a sentence what a count here would abbreviate.
 */
export function scopeHint(matched: number, total: number, noun = 'product') {
	if (matched === 0) return 'none';
	if (matched === total) return `on every ${noun}`;
	return `on ${matched} of ${total} ${noun}s`;
}

/** The selection dot from the offer tables. Copied from each app's `ui/radio`,
 * which are byte-identical; see that file for why the sizes are whole pixels. */
export function Radio({ checked, className }: { checked: boolean; className?: string }) {
	return (
		<span
			aria-hidden
			className={cn(
				'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
				className
			)}
			style={{
				boxShadow: `inset 0 0 0 1px ${checked ? 'var(--accent)' : 'var(--line-3)'}`,
			}}
		>
			{checked && <span className="h-1.5 w-1.5 rounded-full bg-accent" />}
		</span>
	);
}

/** Small icon action — edit, remove, add — in a row's trailing column. */
export function IconButton({
	title,
	onClick,
	danger,
	children,
}: {
	title: string;
	onClick: () => void;
	danger?: boolean;
	children: React.ReactNode;
}) {
	/* The square hit area the v2 offer cards give their row icons, so a remove
	   button here lands on the same grid as the ones two cards up. */
	const card = useContext(SectionFrameContext) === 'card';
	return (
		<button
			type="button"
			title={title}
			onClick={onClick}
			className={cn(
				'text-text-4 transition-colors',
				card
					? 'flex h-6 w-6 shrink-0 items-center justify-center rounded-md hover:bg-surface-hover'
					: 'rounded p-0.5',
				danger ? 'hover:text-status-danger' : 'hover:text-text-1'
			)}
		>
			{children}
		</button>
	);
}

/**
 * A named action on a row — "Add", "Upload".
 *
 * Mirrors the apps' own `Button variant="outline" size="xs"` rather than
 * importing it, since this package can't reach into either app. Kept a button
 * and not an icon: a bare `+` in the actions column crammed against the delete
 * icon, made the first row of each group wider than the rest, and gave the only
 * way to add a fee the presence of a footnote.
 */
export function ActionButton({
	icon: Icon,
	label,
	onClick,
}: {
	icon: React.ElementType;
	label: string;
	onClick: () => void;
}) {
	/* On a v2 card, the add control the offer tables already use — same height,
	   border, type and hover as the "Add sample" button one card up. The panel
	   keeps the smaller pill it was drawn with. */
	const card = useContext(SectionFrameContext) === 'card';
	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				'inline-flex shrink-0 items-center gap-1 rounded-[var(--r-2)] bg-transparent font-medium transition-colors hover:bg-surface-hover',
				card
					? 'h-7 border border-line-2 px-2.5 text-sm text-text-2 hover:text-text-1'
					: 'h-6 border border-line-3 pr-2 pl-1.5 text-xs'
			)}
		>
			<Icon size={card ? 12 : 15} className="shrink-0" />
			{label}
		</button>
	);
}

/**
 * Which products a section lists.
 *
 * Read-only (the client card): only the ones that have the thing — a product
 * with no sample has nothing to say. Editable (admin): every product, because
 * the otherwise-empty row is where its Add button lives, and dropping it would
 * remove the only way to add a sample to a product without one.
 *
 * Attachments don't use this: they are never editable from an offer card, so
 * that section filters directly.
 */
export function rowsFor(
	products: ExtrasProduct[],
	has: (p: ExtrasProduct) => boolean,
	editable: boolean
) {
	return editable ? products : products.filter(has);
}

export function formatFileSize(size: number) {
	return size < 1024 * 1024
		? `${(size / 1024).toFixed(0)} KB`
		: `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function dayRange(min: number | null | undefined, max: number | null | undefined) {
	if (min == null) return '—';
	return `${min}–${max ?? min} days`;
}
